// @ts-nocheck

import React, { useEffect } from 'react';

import { getAppDOMElement, querySelector } from '../../DOMElements';
import useRouter from '../../hooks/useRouter';
import { isGrids } from '../../modules/grids/utils/routes';
import type { MenuActionType } from '../../types/actions/common';
import type { Licence } from '../../types/common';
import { BetslipFilters } from '../../types/history';
import { isMultiviewRoute } from '../../utils/routes/isMultiview';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import DesktopBettingLayout from '../DesktopBettingLayout';
import DesktopGridsLayout from '../DesktopGridsLayout';

import { MainLayout } from './DesktopLayout.style';

const NAV_ITEMS = ['sport-betting', 'grids'];

type DesktopLayoutProps = {
  connected: boolean;
  isExpanded: boolean;
  licence: Licence;
  realMoney: boolean;
  menuReset: () => {
    type: MenuActionType;
  };
  handlePanicButtonPress: () => void;
  openExternalLink: () => void;
  clearLivePreferences: () => void;
};

/**
 * Render a betting or grids section depending on the route
 *
 * @param {boolean} connected Check if we are on grid history (the history detail is excluded)
 * @param {boolean} isExpanded Detect if we are on multiview mode
 * @param {Licence} licence The current licence
 * @param {Function} menuReset The action dispatcher to reset the menu
 *
 * @return {ReactNode} A WTV Provider with grids or betting layout
 */
const DesktopLayout = ({
  connected,
  isExpanded,
  licence,
  realMoney,
  menuReset,
  handlePanicButtonPress,
  openExternalLink,
  clearLivePreferences,
}: DesktopLayoutProps) => {
  const { history, location } = useRouter();
  const isMultiview = isExpanded && isMultiviewRoute(location.pathname);
  const isGridsRoute = isGrids(location.pathname);

  useEffect(() => {
    const app = getAppDOMElement();
    app.style.maxWidth = 'none';
    app.style.padding = 0;
  }, []);

  useEffect(() => {
    // define a listener on the main nav if available
    const mainNav = querySelector('#main-nav');
    const topNavItems = mainNav?.querySelectorAll('li') || [];
    if (topNavItems.length > 0) {
      const itemsArr = [].slice.call(topNavItems);
      itemsArr.forEach((navItem) => {
        const dataAttr = navItem.getAttribute('data-nav');
        if (NAV_ITEMS.indexOf(dataAttr) !== -1) {
          navItem.onclick = (e) => {
            e.preventDefault();
            menuReset();
            let redirection = RoutesRedirectionFactory.getHomeRouteConfig();
            if (dataAttr === NAV_ITEMS[1]) {
              redirection = RoutesRedirectionFactory.getGridsRouteConfig();
            }
            history.push(redirection);
            return false;
          };
        }
      });
    }

    // define a listener on the betting top nav if available
    const topSubNavItems = document.getElementsByClassName('betting-top-nav');
    if (topSubNavItems.length > 0) {
      // ! getElementsByClassName retourne une NodeList, non-itérable
      const itemsArr = [].slice.call(topSubNavItems);
      itemsArr.forEach((navItem) => {
        const dataAttr = navItem
          .getAttribute('data-nav')
          .replace('home', '')
          .replace('history', `history/${BetslipFilters.RUNNING}`); // by default the history page has to be on history running (3)
        navItem.onclick = (e) => {
          e.preventDefault();
          menuReset();
          if (dataAttr === 'live') {
            clearLivePreferences();
          }
          history.push(`/${dataAttr}`);
          return false;
        };
      });
    }
  }, [history, menuReset, clearLivePreferences]);

  return (
    <MainLayout>
      {isGridsRoute ? (
        <DesktopGridsLayout licence={licence} />
      ) : (
        <DesktopBettingLayout
          connected={connected}
          handlePanicButtonPress={handlePanicButtonPress}
          isMultiview={!!isMultiview}
          licence={licence}
          realMoney={realMoney}
          openExternalLink={openExternalLink}
        />
      )}
    </MainLayout>
  );
};

export default DesktopLayout;
